import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/services/apiClient";
import ToastHelper from "@/utils/toastHelper";
import { getEnrollmentProgress, initial, mapEnrollmentPayloadToFormState, mapFormStateToEnrollment, mapStudentProfileToFormState } from "../pages/student/onlineEnrolmentUtils";
import { EnrollmentStatus } from "@/utils/enrolmentStatus";
import { STUDENT_DOCUMENT_DEFINITIONS } from "@/constants/studentDocuments";
import { STUDENT_DOCUMENT_TYPES } from "@/constants/studentDocuments";

const getApiBaseUrl = () => (process.env.REACT_APP_API_URL).replace(/\/$/, "");

const extractSettingPerm = (payload = {}) => payload.settingPerm
  || payload.enrollment?.settingPerm
  || payload.studentEnrollment?.settingPerm
  || payload.data?.settingPerm;

const extractCanViewSummary = (payload = {}) => payload.canViewSummary
  ?? payload.CanViewSummary
  ?? payload.enrollment?.canViewSummary
  ?? payload.enrollment?.CanViewSummary
  ?? payload.studentEnrollment?.canViewSummary
  ?? payload.studentEnrollment?.CanViewSummary
  ?? payload.data?.canViewSummary
  ?? payload.data?.CanViewSummary;

const extractCanEditEnrollment = (payload = {}) => payload.canEditEnrollment
  ?? payload.CanEditEnrollment
  ?? payload.enrollment?.canEditEnrollment
  ?? payload.enrollment?.CanEditEnrollment
  ?? payload.studentEnrollment?.canEditEnrollment
  ?? payload.studentEnrollment?.CanEditEnrollment
  ?? payload.data?.canEditEnrollment
  ?? payload.data?.CanEditEnrollment;

const extractIsAdminReview = (payload = {}) => payload.isAdminReview
  ?? payload.IsAdminReview
  ?? payload.enrollment?.isAdminReview
  ?? payload.enrollment?.IsAdminReview
  ?? payload.studentEnrollment?.isAdminReview
  ?? payload.studentEnrollment?.IsAdminReview
  ?? payload.data?.isAdminReview
  ?? payload.data?.IsAdminReview;

const extractButtonList = (payload = {}) => payload.buttonList
  || payload.ButtonList
  || payload.enrollment?.buttonList
  || payload.enrollment?.ButtonList
  || payload.studentEnrollment?.buttonList
  || payload.studentEnrollment?.ButtonList
  || payload.data?.buttonList
  || payload.data?.ButtonList
  || [];

const hasButtonList = (payload = {}) => Array.isArray(payload.buttonList)
  || Array.isArray(payload.ButtonList)
  || Array.isArray(payload.enrollment?.buttonList)
  || Array.isArray(payload.enrollment?.ButtonList)
  || Array.isArray(payload.studentEnrollment?.buttonList)
  || Array.isArray(payload.studentEnrollment?.ButtonList)
  || Array.isArray(payload.data?.buttonList)
  || Array.isArray(payload.data?.ButtonList);

const extractRequiredFields = (payload = {}) => payload.requiredFields
  || payload.RequiredFields
  || payload.enrollment?.requiredFields
  || payload.enrollment?.RequiredFields
  || payload.studentEnrollment?.requiredFields
  || payload.studentEnrollment?.RequiredFields
  || payload.data?.requiredFields
  || payload.data?.RequiredFields
  || [];

async function fetchEnrollmentPayload(studentBookingId) {
  if (!studentBookingId) return null;

  const apiBaseUrl = getApiBaseUrl();

  try {
    const response = await apiClient.get(`${apiBaseUrl}/StudentEnrollment/Enrollment`, {
      params: { pStudentBookingId: studentBookingId },
    });

    const data = response?.data;
    if (data) {
      if (data.enrollment || data.studentEnrollment) {
        const enrollment = { ...(data.enrollment || data.studentEnrollment) };

        if (enrollment.speakOtherLanguage !== undefined && enrollment.speekOtherLanguage === undefined) {
          enrollment.speekOtherLanguage = enrollment.speakOtherLanguage;
        }

        return {
          isSuccess: data.isSuccess ?? true,
          message: data.message ?? "",
          isAdminReview: extractIsAdminReview(data),
          workFlowStatusNo: data.workFlowStatusNo,
          workFlowStatusName: data.workFlowStatusName,
          isSubmitted: data.isSubmitted,
          studentEnrollment: enrollment,
          settingPerm: extractSettingPerm({ ...data, studentEnrollment: enrollment }),
          canEditEnrollment: extractCanEditEnrollment({ ...data, studentEnrollment: enrollment }),
          canViewSummary: extractCanViewSummary({ ...data, studentEnrollment: enrollment }),
          buttonList: extractButtonList({ ...data, studentEnrollment: enrollment }),
          requiredFields: extractRequiredFields({ ...data, studentEnrollment: enrollment }),
          masterData: data.masterData ?? {},
          booking: data.booking ?? {},
          documents: data.documents ?? data.studentDocuments ?? enrollment.documents ?? enrollment.studentDocuments ?? [],
        };
      }

      return data;
    }
  } catch (error) {
    console.error("Failed to load enrollment payload", error);
    ToastHelper.error("Unable to load enrolment data right now.");
  }

  if (String(studentBookingId) === "1001") {
    return null;
  }

  return null;
}

async function fetchAdminEnrollmentPayload(studentBookingId) {
  if (!studentBookingId) return null;

  try {
    const response = await apiClient.get("/AdminEnrolment/Enrollment", {
      params: { pStudentBookingId: studentBookingId },
    });

    const data = response?.data;
    if (!data) return null;
    const enrollment = data.enrollment || data.studentEnrollment || data.Enrollment || data.StudentEnrollment || data;
    return {
      ...data,
      studentEnrollment: enrollment,
      settingPerm: extractSettingPerm({ ...data, studentEnrollment: enrollment }),
      canEditEnrollment: extractCanEditEnrollment({ ...data, studentEnrollment: enrollment }),
      canViewSummary: extractCanViewSummary({ ...data, studentEnrollment: enrollment }),
      buttonList: extractButtonList({ ...data, studentEnrollment: enrollment }),
      requiredFields: extractRequiredFields({ ...data, studentEnrollment: enrollment }),
      masterData: data.masterData || data.MasterData || {},
      booking: data.booking || data.Booking || {},
      documents: data.documents || data.studentDocuments || data.Documents || data.StudentDocuments || enrollment.documents || enrollment.studentDocuments || [],
    };
  } catch (error) {
    console.error("Failed to load admin enrollment payload", error);
    ToastHelper.error("Unable to load enrolment data right now.");
    return null;
  }
}

async function fetchMasterData() {
  const apiBaseUrl = getApiBaseUrl();

  try {
    const response = await apiClient.get(`${apiBaseUrl}/StudentEnrollment/MasterData`);
    const data = response?.data;
    if (data) {
      if (data.masterData) return data.masterData;
      return data;
    }
    return {};
  } catch (err) {
    console.error("Failed to load masterData", err);
    return {};
  }
}

async function fetchStudentProfile() {
  const apiBaseUrl = getApiBaseUrl();

  try {
    const response = await apiClient.get(`${apiBaseUrl}/StudentProfile`);
    return response?.data || null;
  } catch (error) {
    console.error("Failed to load student profile for enrolment", error);
    return null;
  }
}

const mergeMissingValues = (current, defaults) => Object.keys(current).reduce((result, key) => {
  const value = current[key];
  const isMissing = value === "" || value === null || value === undefined || value === 0;
  result[key] = isMissing && defaults[key] !== undefined ? defaults[key] : value;
  return result;
}, {});


const createDefaultDocuments = () => STUDENT_DOCUMENT_DEFINITIONS.map((definition) => ({
  studentDocTypeId: definition.id,
  key: definition.key,
  label: definition.name,
  file: null,
  fileName: "",
  studentDocId: null,
}));

const mergeEnrollmentDocuments = (sourceDocuments = []) => {
  const existingDocuments = Array.isArray(sourceDocuments) ? sourceDocuments : [];

  return createDefaultDocuments().map((defaultDocument) => {
    const existing = existingDocuments.find((document) =>
      Number(
        document?.studentDocTypeId
        ?? document?.documentTypeId
        ?? document?.StudentDocTypeId
        ?? document?.DocumentTypeId
      ) === defaultDocument.studentDocTypeId
    );

    if (!existing) return defaultDocument;

    return {
      ...defaultDocument,
      studentDocId: existing.studentDocId
        ?? existing.documentId
        ?? existing.studentEnrollmentDocumentId
        ?? existing.StudentDocId
        ?? null,
      fileName: existing.fileName ?? existing.documentName ?? existing.FileName ?? "",
      fileType: existing.fileType ?? existing.contentType ?? existing.FileType ?? "",
      fileSize: existing.fileSize ?? existing.FileSize ?? 0,
      downloadUrl: existing.downloadUrl ?? existing.fileUrl ?? existing.DownloadUrl ?? "",
      issueDate: existing.issueDate ?? existing.IssueDate ?? null,
      expiryDate: existing.expiryDate ?? existing.ExpiryDate ?? null,
      remarks: existing.remarks ?? existing.Remarks ?? "",
      documentDescription: existing.documentDescription ?? existing.DocumentDescription ?? "",
      file: null,
      isPendingUpload: false,
    };
  });
};

const buildEnrollmentDocumentViewUrl = (document, isAdmin, studentEnrollmentId) => {
  const studentDocId = Number(document?.studentDocId ?? 0);
  const enrollmentId = Number(studentEnrollmentId ?? document?.studentEnrollmentId ?? 0);
  if (!studentDocId || !enrollmentId) return "";
  return isAdmin
    ? `/AdminEnrolment/${enrollmentId}/documents/${studentDocId}/view`
    : `/StudentDocuments/enrollment/${studentDocId}/view?studentEnrollmentId=${enrollmentId}`;
};

const applySignatureDocumentState = (state) => ({
  ...state,
  clientSignatureDocumentId: state.clientSignatureDocumentId ?? null,
  clientSignatureViewUrl: "",
  clientSignatureDataUrl: state.clientSignatureDataUrl || "",
  removeClientSignature: false,
  staffSignatureDocumentId: state.staffSignatureDocumentId ?? null,
  staffSignatureViewUrl: "",
  staffSignatureDataUrl: state.staffSignatureDataUrl || "",
  removeStaffSignature: false,
});



const useOnlineEnrollment = (studentBookingId, options = {}) => {
  const { isAdmin = false } = options;
  const navigate = useNavigate();
  const [d, setD] = useState(initial);
  const [active, setActive] = useState("usi");
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [masterData, setMasterData] = useState({});
  const [masterLoading, setMasterLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [studentEnrollment, setStudentEnrollment] = useState(null);
  const [booking, setBooking] = useState(null);
  const [settingPerm, setSettingPerm] = useState({});
  const [canEditEnrollment, setCanEditEnrollment] = useState(true);
  const [canViewSummary, setCanViewSummary] = useState(false);
  const [isAdminReview, setIsAdminReview] = useState(null);
  const [buttonList, setButtonList] = useState([]);
  const [workFlowStatusNo, setWorkFlowStatusNo] = useState(EnrollmentStatus.Draft);
  const [documents, setDocuments] = useState(createDefaultDocuments);
  const [requiredFields, setRequiredFields] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadEnrollment = async () => {
      if (!studentBookingId) {
        setLoadError(false);
        setD(initial);
        setStudentEnrollment(null);
        setBooking(null);
        setSettingPerm({});
        setCanEditEnrollment(true);
        setCanViewSummary(false);
        setIsAdminReview(null);
        setButtonList([]);
        setDocuments(createDefaultDocuments());
        setRequiredFields([]);
        return;
      }

      setLoading(true);
      setLoadError(false);

      const [enrollmentPayload, profile] = await Promise.all([
        isAdmin ? fetchAdminEnrollmentPayload(studentBookingId) : fetchEnrollmentPayload(studentBookingId),
        isAdmin ? Promise.resolve(null) : fetchStudentProfile(),
      ]);
      if (!isMounted) return;

      if (!enrollmentPayload) {
        setLoading(false);
        setLoadError(true);
        return;
      }

      let payload = enrollmentPayload;
      if (!payload.masterData || Object.keys(payload.masterData).length === 0) {
        setMasterLoading(true);
        const loadedMasterData = await fetchMasterData();
        if (!isMounted) return;
        payload = { ...payload, masterData: loadedMasterData || {} };
        setMasterLoading(false);
      }

      const enrollmentData = payload.studentEnrollment || payload.enrollment || payload;
      setBooking(payload.booking || null);
      const loadedSettingPerm = extractSettingPerm({ ...payload, studentEnrollment: enrollmentData }) || {};
      setSettingPerm(loadedSettingPerm);
      const loadedCanEditEnrollment = extractCanEditEnrollment({ ...payload, studentEnrollment: enrollmentData });
      setCanEditEnrollment(loadedCanEditEnrollment !== false);
      const loadedCanViewSummary = extractCanViewSummary({ ...payload, studentEnrollment: enrollmentData }) === true;
      setCanViewSummary(loadedCanViewSummary);
      setIsAdminReview(extractIsAdminReview({ ...payload, studentEnrollment: enrollmentData }) === true);
      setButtonList(extractButtonList({ ...payload, studentEnrollment: enrollmentData }));
      setRequiredFields(extractRequiredFields({ ...payload, studentEnrollment: enrollmentData }));
      const loadedDocuments =
        payload.documents
        || payload.studentDocuments
        || enrollmentData.documents
        || enrollmentData.studentDocuments
        || enrollmentData.vo_DocList
        || enrollmentData.VO_DocList
        || [];
      const mergedDocuments = mergeEnrollmentDocuments(loadedDocuments);
      setDocuments(mergedDocuments);
      const loadedStatus = Number(enrollmentData.workFlowStatusNo || payload.workFlowStatusNo || EnrollmentStatus.Draft);
      setWorkFlowStatusNo(loadedStatus);
      const profileDefaults = mapStudentProfileToFormState(profile, payload.masterData || {});
      setStudentEnrollment(enrollmentData);
      const hasUsableData = Boolean(
        enrollmentData &&
        (
          enrollmentData.studentEnrollmentId ||
          enrollmentData.enrollmentId ||
          enrollmentData.usiNumber ||
          enrollmentData.firstName ||
          enrollmentData.firstName ||
          enrollmentData.email ||
          enrollmentData.mobileNumber ||
          enrollmentData.currentStep ||
          enrollmentData.currentStepNo
        )
      );

      if (!hasUsableData) {
        if (payload.masterData) {
          setMasterData(payload.masterData);
        } else {
          setMasterLoading(true);
          fetchMasterData().then((md) => { if (isMounted) setMasterData(md || {}); }).finally(() => { if (isMounted) setMasterLoading(false); });
        }

        setD({ ...initial, ...profileDefaults });
        setActive("usi");
        setSubmission(null);
        setLoading(false);
        setLoadError(false);
        return;
      }

      const nextState = mapEnrollmentPayloadToFormState(payload);
      const completedState = applySignatureDocumentState(mergeMissingValues(nextState, profileDefaults), mergedDocuments, isAdmin, enrollmentData.studentEnrollmentId);
      setD(completedState);
      setActive(nextState.activeSection || "usi");
      setSubmission(loadedCanViewSummary && enrollmentData.studentEnrollmentId ? {
        ref: enrollmentData.enrolmentNumber,
        submittedAt: enrollmentData.submittedDate || new Date().toISOString(),
        data: { ...completedState, settingPerm: loadedSettingPerm },
        booking: payload.booking || null,
        workFlowStatusNo: loadedStatus,
        settingPerm: loadedSettingPerm,
        canViewSummary: loadedCanViewSummary,
      } : null);
      if (payload.masterData) {
        setMasterData(payload.masterData);
      } else {
        setMasterLoading(true);
        fetchMasterData().then((md) => { if (isMounted) setMasterData(md || {}); }).finally(() => { if (isMounted) setMasterLoading(false); });
      }
      setLoading(false);
      ToastHelper.success("Existing enrolment details loaded");
    };

    loadEnrollment();

    return () => { isMounted = false; };
  }, [studentBookingId, isAdmin]);

  const set = (k, v) => setD((current) => ({ ...current, [k]: v }));

  const requiredCount = useMemo(() => {
    return getEnrollmentProgress(d, documents, requiredFields);
  }, [d, documents, requiredFields]);

  const canSubmit = Boolean(studentBookingId || d.studentBookingId);

  const save = async ({ submit = false, silent = false, nextWorkFlowStatusNo = 0 } = {}) => {
    try {
      setSaving(true);
      if (!isAdmin && !canEditEnrollment) {
        ToastHelper.error("This enrolment is read-only for the current workflow status.");
        return { success: false };
      }
      const apiBaseUrl = getApiBaseUrl();
      if (!studentBookingId && !d.studentBookingId) {
        ToastHelper.error("Please open enrolment from a course booking before saving.");
        return { success: false };
      }
      const oldItem = { ...(studentEnrollment || {}) };
      const mappedFormState = mapFormStateToEnrollment(d, documents);
      if (!mappedFormState.countryId) {
        const australia = (masterData.countries || []).find((item) => String(item.name || "").toLowerCase() === "australia");
        mappedFormState.countryId = australia ? Number(australia.id) : null;
      }
      const enrollment = {
        ...(studentEnrollment || {}),
        ...mappedFormState,
      };

      enrollment.studentBookingId = Number(studentBookingId || d.studentBookingId);

      const requestedNextStatus = Number(nextWorkFlowStatusNo);
      const currentWorkFlowStatus = enrollment.CurrentWorkFlowStatus || {};
      enrollment.CurrentWorkFlowStatus = {
        WorkFlowStatusNo: Number(currentWorkFlowStatus.WorkFlowStatusNo ?? workFlowStatusNo ?? 0) || 0,
        WorkFlowStatusName: currentWorkFlowStatus.WorkFlowStatusName ?? "",
        NextWorkFlowStatusNo: Number.isFinite(requestedNextStatus) && requestedNextStatus > 0
          ? requestedNextStatus
          : Number(currentWorkFlowStatus.NextWorkFlowStatusNo ?? 0) || 0,
      };
      delete enrollment.currentWorkFlowStatus;

      if (submit) {
        enrollment.isSubmitted = true;
        enrollment.submittedDate = new Date().toISOString();
      }

      const request = {
        Message: "",
        Submit: submit,
        Enrollment: enrollment,
        OldVO: oldItem,
      };

      const multipartData = new FormData();
      multipartData.append("EnrollmentJson", JSON.stringify(request));

      documents.forEach((document, index) => {
        multipartData.append(
          `Documents[${index}].StudentDocTypeId`,
          String(document.studentDocTypeId)
        );

        if (document.studentDocId) {
          multipartData.append(
            `Documents[${index}].StudentDocId`,
            String(document.studentDocId)
          );
        }

        if (document.issueDate) multipartData.append(`Documents[${index}].IssueDate`, document.issueDate);
        if (document.expiryDate) multipartData.append(`Documents[${index}].ExpiryDate`, document.expiryDate);
        if (document.remarks) multipartData.append(`Documents[${index}].Remarks`, document.remarks);
        if (document.documentDescription) {
          multipartData.append(
            `Documents[${index}].DocumentDescription`,
            document.documentDescription
          );
        }

        if (typeof File !== "undefined" && document.file instanceof File) {
          multipartData.append(
            `Documents[${index}].File`,
            document.file,
            document.file.name
          );
        }
      });

      const saveUrl = isAdmin ? `${apiBaseUrl}/AdminEnrolment/Save` : `${apiBaseUrl}/StudentEnrollment/Save`;
      const response = await apiClient.post(saveUrl, multipartData);

      if (response?.data) {
        const returned = response.data;
        const normalized = returned.studentEnrollment || returned.enrollment ? returned : null;

        if (returned.studentEnrollmentId) {
          const savedEnrollmentId = Number(returned.studentEnrollmentId);
          setStudentEnrollment((current) => ({ ...current, studentEnrollmentId: savedEnrollmentId }));
          setD((current) => ({ ...current, studentEnrollmentId: String(savedEnrollmentId) }));
        }
        if (returned.workFlowStatusNo) setWorkFlowStatusNo(Number(returned.workFlowStatusNo));

        const returnedDocuments =
          returned.documents
          || returned.studentDocuments
          || normalized?.documents
          || normalized?.studentDocuments;

        if (Array.isArray(returnedDocuments)) {
          const mergedDocuments = mergeEnrollmentDocuments(returnedDocuments);
          setDocuments(mergedDocuments);
          setD((current) => applySignatureDocumentState(current, mergedDocuments, isAdmin, returned.studentEnrollmentId ?? current.studentEnrollmentId));
        } else {
          setDocuments((current) => current.map((document) => ({
            ...document,
            file: null,
            isPendingUpload: false,
          })));
        }

        if (normalized?.masterData) setMasterData(normalized.masterData);
        const returnedSettingPerm = extractSettingPerm(normalized || returned);
        if (returnedSettingPerm) setSettingPerm(returnedSettingPerm);
        const returnedCanEditEnrollment = extractCanEditEnrollment(normalized || returned);
        if (returnedCanEditEnrollment !== undefined) setCanEditEnrollment(returnedCanEditEnrollment !== false);
        const returnedCanViewSummary = extractCanViewSummary(normalized || returned);
        if (returnedCanViewSummary !== undefined) setCanViewSummary(returnedCanViewSummary === true);
        if (hasButtonList(normalized || returned)) setButtonList(extractButtonList(normalized || returned));
        if (normalized?.studentEnrollment || normalized?.enrollment) {
          const nextState = applySignatureDocumentState(mapEnrollmentPayloadToFormState(normalized), returnedDocuments || documents, isAdmin, normalized.studentEnrollment?.studentEnrollmentId ?? normalized.enrollment?.studentEnrollmentId ?? normalized.studentEnrollmentId);
          setD(nextState);
          setActive(nextState.activeSection || "usi");
          setLoadError(false);
        }

        if (!silent) ToastHelper.success(submit ? "Enrolment submitted successfully." : "Progress saved - you can resume this enrolment any time");
        return { success: true, payload: normalized || returned };
      }

      if (!silent) ToastHelper.success(submit ? "Enrolment submitted successfully." : "Progress saved - you can resume this enrolment any time");
      return { success: true };
    } catch (err) {
      console.error("Failed to save enrolment", err);
      const validationErrors = err.response?.data?.errors
        || err.response?.data?.validationResult?.errors;
      const validationMessages = Array.isArray(validationErrors)
        ? validationErrors.map((error) => error?.message || String(error)).filter(Boolean)
        : validationErrors && typeof validationErrors === "object"
          ? Object.entries(validationErrors).flatMap(([field, messages]) =>
            (Array.isArray(messages) ? messages : [messages]).map((message) => `${field}: ${message}`))
          : [];
      const enrollmentValidation = err.response?.data?.enrollmentValidation;
      const apiMessage = err.response?.data?.message
        || err.response?.data?.title
        || (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join(", ") : "");
      if (submit && enrollmentValidation) {
        return { success: false, validation: enrollmentValidation };
      }
      if (validationMessages.length > 0) {
        ToastHelper.error(validationMessages.join("\n"), {
          className: "whitespace-pre-line",
        });
      } else {
        ToastHelper.error(apiMessage || (submit ? "Unable to submit enrolment right now." : "Unable to save progress right now."));
      }
      return { success: false };
    } finally {
      setSaving(false);
    }
  };

  const saveAndReturn = async () => {
    const result = await save();
    if (result.success) {
      navigate(isAdmin ? `/admin/caurse-progress/${studentBookingId}` : "/student/my-courses");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { ToastHelper.error("Open enrolment from a booking before submitting."); return { success: false }; }
    const result = await save({ submit: true, silent: true });
    if (!result.success) return result;

    const ref = result.payload?.enrolmentNumber || result.payload?.EnrolmentNumber;
    if (!ref) {
      ToastHelper.error("The server did not return an enrolment reference.");
      return;
    }
    const refreshedPayload = await fetchEnrollmentPayload(studentBookingId || d.studentBookingId);
    const refreshedEnrollment = refreshedPayload?.studentEnrollment || refreshedPayload?.enrollment || {};
    const submittedSettingPerm = extractSettingPerm({
      ...(refreshedPayload || {}),
      studentEnrollment: refreshedEnrollment,
    }) || settingPerm || {};
    const submittedCanEditEnrollment = extractCanEditEnrollment({
      ...(refreshedPayload || {}),
      studentEnrollment: refreshedEnrollment,
    });
    const submittedCanViewSummary = extractCanViewSummary({
      ...(refreshedPayload || {}),
      studentEnrollment: refreshedEnrollment,
    }) === true;
    const submittedStatus = Number(refreshedEnrollment.workFlowStatusNo || refreshedPayload?.workFlowStatusNo || EnrollmentStatus.Submitted);
    const submittedButtonList = extractButtonList({
      ...(refreshedPayload || {}),
      studentEnrollment: refreshedEnrollment,
    });
    const submittedBooking = refreshedPayload?.booking || booking;
    const submittedState = refreshedPayload ? mapEnrollmentPayloadToFormState(refreshedPayload) : d;
    if (refreshedPayload?.documents || refreshedPayload?.studentDocuments || refreshedEnrollment.documents || refreshedEnrollment.studentDocuments) {
      const mergedDocuments = mergeEnrollmentDocuments(refreshedPayload.documents || refreshedPayload.studentDocuments || refreshedEnrollment.documents || refreshedEnrollment.studentDocuments);
      setDocuments(mergedDocuments);
      setD((current) => applySignatureDocumentState(current, mergedDocuments, isAdmin, refreshedEnrollment.studentEnrollmentId));
    }
    setD(applySignatureDocumentState(submittedState, refreshedPayload?.documents || refreshedPayload?.studentDocuments || refreshedEnrollment.documents || refreshedEnrollment.studentDocuments || documents, isAdmin, refreshedEnrollment.studentEnrollmentId));
    setBooking(submittedBooking || null);
    setWorkFlowStatusNo(submittedStatus);
    setSettingPerm(submittedSettingPerm);
    setCanEditEnrollment(submittedCanEditEnrollment !== false);
    setButtonList(submittedButtonList);
    setCanViewSummary(submittedCanViewSummary);
    setSubmission(submittedCanViewSummary ? {
      ref,
      submittedAt: new Date().toISOString(),
      data: {
        ...submittedState,
        studentEnrollmentId: refreshedEnrollment.studentEnrollmentId ?? result.payload?.studentEnrollmentId ?? d.studentEnrollmentId,
        studentBookingId: studentBookingId ?? d.studentBookingId,
        enrolmentNumber: ref,
        settingPerm: submittedSettingPerm,
      },
      booking: submittedBooking,
      workFlowStatusNo: submittedStatus,
      settingPerm: submittedSettingPerm,
      canViewSummary: submittedCanViewSummary,
    } : null);
    ToastHelper.success(`Enrolment ${ref} submitted successfully.`);
    window.scrollTo({ top: 0, behavior: "smooth" });
    return { success: true };
  };

  const startNew = () => {
    setSubmission(null);
    setStudentEnrollment(null);
    setCanEditEnrollment(true);
    setCanViewSummary(false);
    setButtonList([]);
    setD(initial);
    setDocuments(createDefaultDocuments());
    setActive("usi");
  };
  const cancel = () => {
    if (confirm("Discard all changes?")) {
      setD(initial);
      setDocuments(createDefaultDocuments());
      setActive("usi");
      ToastHelper.info("Form cleared");
    }
  };
  const jumpTo = (id) => { setActive(id); document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return {
    d,
    set,
    active,
    setActive,
    submission,
    loading,
    loadError,
    masterData,
    masterLoading,
    saving,
    requiredCount,
    canSubmit,
    canEdit: canEditEnrollment,
    settingPerm,
    canViewSummary,
    isAdminReview,
    buttonList,
    workFlowStatusNo,
    booking,
    documents,
    setDocuments,
    save,
    saveAndReturn,
    submit,
    startNew,
    cancel,
    jumpTo,
  };
};

export default useOnlineEnrollment;
